import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Baby, Bell, FileCheck, LifeBuoy, UserPlus, X } from 'lucide-react';
import { fetchApi } from '../config/api';
import { Sheet } from './UI';
import { formatDateTime } from '../utils/formatDate';
import { PushEnablePrompt } from './PushNotificationCard';

interface NotificationItem {
  id: number;
  type: string;
  relatedUserId: string | null;
  relatedUserEmail: string;
  relatedUserName: string;
  dogId: number | null;
  dogName: string;
  phaseId: string | null;
  documentName: string;
  /** Neue Vorschlaege zum Pruefen; 0 = bestehender Eintrag ergaenzt. */
  itemCount: number | null;
  supportTicketId: number | null;
  subject: string;
  link: string | null;
  isRead: boolean;
  createdAt: string;
}

const POLL_INTERVAL_MS = 60000;

/**
 * Glocke mit umrahmtem Badge fuer ungelesene Benachrichtigungen (neue Registrierungen fuer Admins, neue
 * Traechtigkeitsphasen und neue Daten aus hochgeladenen Dokumenten fuer Halter, Support-Anfragen und -Antworten),
 * wie man es von sozialen Netzwerken kennt.
 */
export const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadNotifications = async () => {
    try {
      const res = await fetchApi('/notifications');
      const data = res?.data ?? res;
      setNotifications(data?.notifications ?? []);
      setUnreadCount(data?.unreadCount ?? 0);
      setLoaded(true);
    } catch {
      // Badge bleibt dann einfach beim letzten bekannten Stand - nicht kritisch fuer die Navigation.
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const handleOpen = () => {
    setOpen(true);
    if (!loaded) loadNotifications();
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    setOpen(false);
    if (!notification.isRead) {
      setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      try {
        await fetchApi(`/notifications/${notification.id}/read`, { method: 'POST' });
      } catch {
        // Best-effort - der Nutzer sieht den lokal aktualisierten Zustand trotzdem sofort.
      }
    }
    if (notification.link) {
      navigate(notification.link);
    } else if (notification.type === 'PregnancyPhaseChanged' && notification.dogId) {
      navigate(`/dogs/${notification.dogId}/pregnancy`);
    } else if (notification.relatedUserId) {
      navigate(`/admin/users/${notification.relatedUserId}`);
    }
  };

  const handleDismiss = async (notification: NotificationItem) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    if (!notification.isRead) setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await fetchApi(`/notifications/${notification.id}`, { method: 'DELETE' });
    } catch {
      // Best-effort - beim nächsten Abruf erscheint sie sonst einfach wieder.
    }
  };

  const handleDismissAll = async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      await fetchApi('/notifications', { method: 'DELETE' });
    } catch {
      // Best-effort.
    }
  };

  const handleMarkAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try {
      await fetchApi('/notifications/read-all', { method: 'POST' });
    } catch {
      // Best-effort.
    }
  };

  return (
    <>
      <button
        type="button"
        className="icon-btn relative"
        onClick={handleOpen}
        aria-label={unreadCount > 0 ? t('nav:notifications.bellLabelUnread', { count: unreadCount }) : t('nav:notifications.bellLabel')}
        title={unreadCount > 0 ? t('nav:notifications.bellLabelUnread', { count: unreadCount }) : t('nav:notifications.bellLabel')}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-dark-900 bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <Sheet
        isOpen={open}
        title={t('nav:notifications.title')}
        onClose={() => setOpen(false)}
        size="sm"
        footer={
          notifications.length > 0 ? (
            <div className="flex gap-2">
              {notifications.some((n) => !n.isRead) && (
                <button type="button" className="flex-1 rounded-lg py-2 text-center text-sm font-medium text-primary-400 transition-colors hover:bg-dark-700" onClick={handleMarkAllRead}>
                  {t('nav:notifications.markAllRead')}
                </button>
              )}
              <button type="button" className="flex-1 rounded-lg py-2 text-center text-sm font-medium text-dark-300 transition-colors hover:bg-dark-700" onClick={handleDismissAll}>
                {t('nav:notifications.dismissAll')}
              </button>
            </div>
          ) : undefined
        }
      >
        <PushEnablePrompt />
        {notifications.length === 0 ? (
          <p className="py-6 text-center text-sm text-dark-400">{t('nav:notifications.empty')}</p>
        ) : (
          <div className="flex flex-col gap-1">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`flex items-start rounded-xl transition-colors hover:bg-dark-700 ${notification.isRead ? '' : 'bg-primary-500/5'}`}
              >
              <button
                type="button"
                onClick={() => handleNotificationClick(notification)}
                className="flex min-w-0 flex-1 items-start gap-3 rounded-xl px-3 py-2.5 text-left"
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500/15 text-primary-300">
                  <NotificationIcon type={notification.type} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-dark-100">
                    {notificationText(notification, t)}
                  </span>
                  <span className="mt-0.5 block text-xs text-dark-400">{formatDateTime(notification.createdAt)}</span>
                </span>
                {!notification.isRead && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-400" />}
              </button>
              <button
                type="button"
                onClick={() => handleDismiss(notification)}
                aria-label={t('nav:notifications.dismiss')}
                title={t('nav:notifications.dismiss')}
                className="mr-1 mt-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-dark-400 transition-colors hover:bg-dark-600 hover:text-dark-100"
              >
                <X size={16} />
              </button>
              </div>
            ))}
          </div>
        )}
      </Sheet>
    </>
  );
};

const NotificationIcon: React.FC<{ type: string }> = ({ type }) => {
  if (type === 'PregnancyPhaseChanged') return <Baby size={16} />;
  if (type === 'HealthDataExtracted') return <FileCheck size={16} />;
  if (type === 'SupportRequestReceived' || type === 'SupportReplyReceived') return <LifeBuoy size={16} />;
  return <UserPlus size={16} />;
};

function notificationText(notification: NotificationItem, t: TFunction): string {
  switch (notification.type) {
    case 'PregnancyPhaseChanged':
      return t('nav:notifications.pregnancyPhase', {
        dog: notification.dogName,
        phase: t(`pregnancy:phases.${notification.phaseId}`, { defaultValue: notification.phaseId ?? '' }),
      });
    case 'HealthDataExtracted':
      return notification.itemCount
        ? t('nav:notifications.healthData', { dog: notification.dogName, document: notification.documentName, count: notification.itemCount })
        : t('nav:notifications.healthDataUpdated', { dog: notification.dogName, document: notification.documentName });
    case 'SupportRequestReceived':
      return t('nav:notifications.supportRequest', { name: notification.relatedUserName || notification.relatedUserEmail, subject: notification.subject });
    case 'SupportReplyReceived':
      return t('nav:notifications.supportReply', { subject: notification.subject });
    default:
      return t('nav:notifications.userRegistered', { name: notification.relatedUserName || notification.relatedUserEmail });
  }
}
